import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1503423571797-2d2bb372094a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwbWVldGluZyUyMGdsYXNzfGVufDB8fHx8MTc4NzI3NjUxOHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Selamat datang");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 relative border-r border-border">
        <img src={LOGIN_BG} alt="Boardroom" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2" />
          <div className="max-w-md">
            <h1 className="font-heading font-black text-5xl leading-[0.95] tracking-tight mb-6">
              Minute of Meeting.<br />
              <span className="text-primary">Dokumentasi Notulen Meeting.</span>
            </h1>
          </div>
          <div className="text-xs text-white/40 tracking-[0.2em] uppercase mono">
            Meeting &amp; Minutes Management
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <h2 className="font-heading font-black text-3xl tracking-tight mb-1">Masuk</h2>
          <p className="text-muted-foreground text-sm mb-8">Akses sistem manajemen notulen rapat.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="username"
                autoCapitalize="none"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-none pr-11 focus-visible:ring-2 focus-visible:ring-primary"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-0 top-0 h-full px-3 flex items-center text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-primary border border-primary/40 bg-primary/5 px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-medium group"
            >
              {loading ? "Mohon tunggu…" : "Masuk"}
              {!loading && <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-150" />}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground">
            Belum punya akun? Hubungi Super Admin untuk pembuatan akun.
          </div>
        </div>
      </div>
    </div>
  );
}
